import { type JsonMessage, type Room, type Transport, asText, randomId } from './transport';

/**
 * Guilds (research 04 §5.6): a guild is a name, a tag and a shared secret.
 * Members join the guild's password-protected room whenever they play; the
 * roster is whoever has been seen online this session. Guild chat and the tag
 * on nameplates come from here. Membership lives in each member's save.
 */

const HEARTBEAT_MS = 8000;
const STALE_MS = 25000;

export interface GuildData {
  id: string;
  key: string;
  name: string;
  tag: string;
  rank: 'leader' | 'member';
}

export interface GuildPresence {
  uid: string;
  name: string;
  classId: string;
  level: number;
  zone: string | null;
}

export interface GuildRosterEntry extends GuildPresence {
  peer: string | null;
  rank: 'leader' | 'member';
  seen: number;
}

export interface GuildDeps {
  transport: Transport;
  self(): GuildPresence;
  now(): number;
  changed(): void;
  notify(text: string): void;
  chat(fromName: string, text: string, fromPeer: string): void;
  /** Persists membership (null after leaving). */
  save(g: GuildData | null): void;
  /** Messages the guild protocol does not use (whispers). */
  other?(from: string, m: JsonMessage): void;
}

export const GUILD_NAME = /^[A-Za-z][A-Za-z '-]{2,19}$/;
export const GUILD_TAG = /^[A-Z0-9]{2,4}$/;

export class GuildManager {
  data: GuildData | null = null;
  room: Room | null = null;
  readonly roster = new Map<string, GuildRosterEntry>();
  private offs: (() => void)[] = [];
  private lastBeat = 0;

  constructor(private d: GuildDeps) {}

  /** Creates a new guild led by us. Returns an error message on failure. */
  async create(name: string, tag: string): Promise<string | null> {
    const n = name.trim();
    const t = tag.trim().toUpperCase();
    if (!GUILD_NAME.test(n)) return 'Guild names use 3–20 letters.';
    if (!GUILD_TAG.test(t)) return 'Tags are 2–4 letters or digits.';
    if (this.data) return 'Leave your current guild first.';
    await this.open({ id: randomId(12), key: randomId(20), name: n, tag: t, rank: 'leader' });
    return null;
  }

  async open(g: GuildData): Promise<void> {
    if (this.data?.id === g.id) return;
    this.close();
    this.data = g;
    this.d.save(g);
    const me = this.d.self();
    this.roster.set(me.uid, { ...me, peer: this.d.transport.selfId, rank: g.rank, seen: this.d.now() });
    this.d.changed();
    const room = await this.d.transport.join(`g:${g.id}`, { password: g.key });
    if (this.data?.id !== g.id) {
      void room.leave();
      return;
    }
    this.room = room;
    this.offs.push(
      room.onMessage((from, m) => this.onMessage(from, m)),
      room.onPeerJoin((peer) => this.beat(peer)),
      room.onPeerLeave((peer) => {
        for (const e of this.roster.values()) if (e.peer === peer) e.peer = null;
        this.d.changed();
      }),
    );
    this.beat();
  }

  /** Leaves the guild room without leaving the guild (logout, character switch). */
  close(): void {
    for (const off of this.offs) off();
    this.offs = [];
    void this.room?.leave();
    this.room = null;
    this.data = null;
    this.roster.clear();
  }

  leave(): void {
    if (!this.data) return;
    this.room?.send({ t: 'gleave', uid: this.d.self().uid });
    this.close();
    this.d.save(null);
    this.d.changed();
  }

  inviteMessage(): JsonMessage | null {
    const g = this.data;
    return g ? { t: 'ginv', gid: g.id, key: g.key, name: g.name, tag: g.tag } : null;
  }

  async accept(inv: { gid: string; key: string; name: string; tag: string }): Promise<void> {
    if (!GUILD_NAME.test(inv.name) || !GUILD_TAG.test(inv.tag)) return;
    if (this.data) this.leave();
    await this.open({ id: inv.gid, key: inv.key, name: inv.name, tag: inv.tag, rank: 'member' });
    this.d.notify(`You joined <${inv.tag}> ${inv.name}.`);
  }

  kick(uid: string): void {
    if (this.data?.rank !== 'leader' || uid === this.d.self().uid) return;
    this.room?.send({ t: 'gkick', uid });
    this.roster.delete(uid);
    this.d.changed();
  }

  chat(text: string): void {
    this.room?.send({ t: 'gchat', name: this.d.self().name, text });
  }

  private beat(to?: string): void {
    const g = this.data;
    if (!g) return;
    this.lastBeat = this.d.now();
    const me = this.d.self();
    const mine = this.roster.get(me.uid);
    if (mine) Object.assign(mine, me, { seen: this.lastBeat });
    this.room?.send({ t: 'gm', m: me, rank: g.rank }, to);
  }

  private senderRank(from: string): 'leader' | 'member' | null {
    for (const e of this.roster.values()) if (e.peer === from) return e.rank;
    return null;
  }

  private onMessage(from: string, m: JsonMessage): void {
    const g = this.data;
    if (!g) return;
    switch (m.t) {
      case 'gm': {
        const p = m.m as Partial<GuildPresence> | undefined;
        if (!p || typeof p.uid !== 'string' || p.uid === this.d.self().uid) return;
        this.roster.set(p.uid, { uid: p.uid, name: asText(p.name, '?', 24), classId: asText(p.classId), level: Number(p.level) || 1, zone: typeof p.zone === 'string' ? p.zone : null, peer: from, rank: m.rank === 'leader' ? 'leader' : 'member', seen: this.d.now() });
        this.d.changed();
        return;
      }
      case 'gleave':
        for (const [uid, e] of this.roster) if (e.peer === from) this.roster.delete(uid);
        this.d.changed();
        return;
      case 'gkick': {
        if (this.senderRank(from) !== 'leader' || typeof m.uid !== 'string') return;
        if (m.uid === this.d.self().uid) {
          const name = g.name;
          this.close();
          this.d.save(null);
          this.d.notify(`You were removed from ${name}.`);
        } else this.roster.delete(m.uid);
        this.d.changed();
        return;
      }
      case 'gchat': {
        const e = [...this.roster.values()].find((r) => r.peer === from);
        if (e && typeof m.text === 'string') this.d.chat(e.name, m.text.slice(0, 200), from);
        return;
      }
      default:
        this.d.other?.(from, m);
        return;
    }
  }

  tick(): void {
    if (!this.data) return;
    const now = this.d.now();
    if (now - this.lastBeat >= HEARTBEAT_MS) this.beat();
    for (const e of this.roster.values()) if (e.peer && now - e.seen > STALE_MS) e.peer = null;
  }
}
